import type {
  AudienceEstimate,
  Audience,
  AutomationDryRun,
  ComposeRequest,
  CreatePromotionRequest,
  EngagementAutomation,
  EngagementInsights,
  EngagementMessage,
  EngagementOverview,
  EngagementPromotion,
  EngagementRetention,
  EngagementSettings,
  EngagementSettingsUpdate,
  EngagementTemplate,
  EngagementTenantRow,
  ExperimentVariantRow,
  GrowthFunnel,
  GrowthRoas,
  MessageDetail,
  MessagePreview,
  Paged,
  PreviewRequest,
  PromotionPreview,
  Risk,
  SendResult,
  TenantState,
  TimelineEntry,
  TopicClass,
  Channel,
  TrialPipeline,
  MessageApproval,
  MessageDelivery,
} from 'types/engagement.types';
import { API_V1, getErrorMessage } from './APIs/APIv1';

/**
 * Admin client for the Engagement Center (`/admin/engagement/*`, platform admins only).
 *
 * Every route answers `{ success: true, data }`. Where the API's shape differs from what a screen
 * wants (message detail, funnel/ROAS rows, experiments, promotion body), the conversion happens
 * here and nowhere else — the shapes are pinned in
 * packages/api/src/modules/engagement/__tests__/contract/response-keys.json. Errors are rethrown as
 * `EngagementApiError` with HTTP status and the server message, so screens can tell "409 test
 * missing" from "failed".
 */

export class EngagementApiError extends Error {
  status?: number;
  code?: string;

  constructor(message: string, status?: number, code?: string) {
    super(message);
    this.name = 'EngagementApiError';
    this.status = status;
    this.code = code;
  }
}

export const unwrap = <T>(body: unknown): T => {
  if (body && typeof body === 'object' && 'success' in body && 'data' in body) {
    return (body as { data: T }).data;
  }
  return body as T;
};

/** Lists may come as an array, `{ items }` or `{ rows }`; screens only want the array. */
export const asList = <T>(value: unknown, key: 'items' | 'rows' = 'items'): T[] => {
  if (Array.isArray(value)) return value as T[];
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (Array.isArray(obj[key])) return obj[key] as T[];
    if (Array.isArray(obj.items)) return obj.items as T[];
    if (Array.isArray(obj.rows)) return obj.rows as T[];
  }
  return [];
};

export const toEngagementError = (e: unknown): EngagementApiError => {
  if (e instanceof EngagementApiError) return e;
  const err = e as { response?: { status?: number; data?: unknown }; message?: string };
  const data = err?.response?.data as { code?: string } | undefined;
  return new EngagementApiError(getErrorMessage(data) || err?.message || 'Request failed', err?.response?.status, data?.code);
};

export const call = async <T>(fn: () => Promise<{ data: unknown }>): Promise<T> => {
  try {
    const res = await fn();
    return unwrap<T>(res.data);
  } catch (e) {
    throw toEngagementError(e);
  }
};

const BASE = '/admin/engagement';

// ------------------------------------------------------------------ Kunden

export const getEngagementOverview = (days = 28) =>
  call<EngagementOverview>(() => API_V1.get(`${BASE}/overview`, { params: { days } }));

export const getEngagementTenants = (filter: { state?: TenantState | ''; risk?: Risk | ''; q?: string } = {}) =>
  call<unknown>(() =>
    API_V1.get(`${BASE}/tenants`, {
      params: { state: filter.state || undefined, risk: filter.risk || undefined, q: filter.q || undefined },
    })
  ).then((d) => asList<EngagementTenantRow>(d));

export const getTenantTimeline = (tenantId: string, days = 30) =>
  call<unknown>(() => API_V1.get(`${BASE}/tenants/${encodeURIComponent(tenantId)}/timeline`, { params: { days } })).then((d) =>
    asList<TimelineEntry>(d)
  );

export const getUserTimeline = (userId: string, days = 30) =>
  call<unknown>(() => API_V1.get(`${BASE}/users/${encodeURIComponent(userId)}/timeline`, { params: { days } })).then((d) =>
    asList<TimelineEntry>(d)
  );

// ------------------------------------------------------------------ Nachrichten

/** topicClass/channels make the estimate count what the send will count (offers need consent, email needs an address). */
export const estimateAudience = (audience: Audience, topicClass?: TopicClass, channels?: Channel[]) =>
  call<AudienceEstimate>(() => API_V1.post(`${BASE}/messages/estimate`, { audience, topicClass, channels }));

export const previewMessage = (req: PreviewRequest) => call<MessagePreview>(() => API_V1.post(`${BASE}/messages/preview`, req));

export const sendTestMessage = (req: Omit<ComposeRequest, 'audience' | 'testId' | 'dryRun'>) =>
  call<{ testId: string }>(() => API_V1.post(`${BASE}/messages/test`, req));

export const sendMessage = (req: ComposeRequest) => call<SendResult>(() => API_V1.post(`${BASE}/messages`, req));

export const approveMessage = (id: string) => call<SendResult>(() => API_V1.post(`${BASE}/messages/${encodeURIComponent(id)}/approve`));

/** Outbox filter by origin: Automatik, System (customer mails the code sends), Admin (manual), Test. */
export type MessageSourceFilter = 'automation' | 'system' | 'manual' | 'test';

export const getMessages = (page = 1, source?: MessageSourceFilter) =>
  call<unknown>(() => API_V1.get(`${BASE}/messages`, { params: source ? { page, source } : { page } })).then((d): Paged<EngagementMessage> => {
    const items = asList<EngagementMessage>(d);
    const meta = (d && typeof d === 'object' && !Array.isArray(d) ? d : {}) as { total?: number; page?: number };
    return { items, total: meta.total, page: meta.page ?? page };
  });

interface MessageDetailResponse {
  message: EngagementMessage;
  deliveries?: MessageDelivery[];
  artifacts?: MessageDetail['artifacts'];
  approval?: MessageApproval | null;
}

/** The API nests the message (`{ message, deliveries, artifacts, approval }`); screens get it flat. */
export const toMessageDetail = (d: MessageDetailResponse): MessageDetail => ({
  ...d.message,
  deliveries: d.deliveries ?? [],
  artifacts: d.artifacts ?? [],
  approval: d.approval ?? null,
});

export const getMessage = (id: string) =>
  call<MessageDetailResponse>(() => API_V1.get(`${BASE}/messages/${encodeURIComponent(id)}`)).then(toMessageDetail);

// ------------------------------------------------------------------ Vorlagen

export const getTemplates = () => call<unknown>(() => API_V1.get(`${BASE}/templates`)).then((d) => asList<EngagementTemplate>(d));

export const getTemplate = (key: string) => call<EngagementTemplate>(() => API_V1.get(`${BASE}/templates/${encodeURIComponent(key)}`));

type VariantChannels = NonNullable<EngagementTemplate['variants']['de']>;

/** API: notification needs { title, body }, email { subject, body }; unknown keys are dropped. */
const cleanVariants = (variants: EngagementTemplate['variants']) => {
  const out: Record<string, { notification?: unknown; email?: unknown }> = {};
  for (const [lang, v] of Object.entries(variants ?? {}) as [string, VariantChannels | undefined][]) {
    if (!v) continue;
    const entry: { notification?: unknown; email?: unknown } = {};
    if (v.notification) {
      entry.notification = { title: v.notification.title ?? '', body: v.notification.body ?? '', ctaLabel: v.notification.ctaLabel || undefined };
    }
    if (v.email) {
      entry.email = { subject: v.email.subject ?? v.email.title ?? '', body: v.email.body ?? '', ctaLabel: v.email.ctaLabel || undefined };
    }
    if (entry.notification || entry.email) out[lang] = entry;
  }
  return out;
};

/**
 * The template routes are strict: POST takes exactly the editable fields, PUT the same without
 * `key` (partial), and system templates must not send `variables` (filled by code) or a changed
 * class. Server fields (_id, system, lastTested*, testedCurrentWording, timestamps) never go back.
 */
export const templateBody = (tpl: EngagementTemplate, mode: 'create' | 'update'): Record<string, unknown> => {
  const body: Record<string, unknown> = {
    name: tpl.name,
    active: tpl.active,
    variants: cleanVariants(tpl.variants),
    ctaUrl: tpl.ctaUrl || undefined,
    recipientBenefit: tpl.recipientBenefit,
  };
  if (mode === 'create') body.key = tpl.key;
  if (!tpl.system) {
    body.topicClass = tpl.topicClass;
    body.variables = tpl.variables ?? [];
  }
  return body;
};

export const createTemplate = (tpl: EngagementTemplate) =>
  call<EngagementTemplate>(() => API_V1.post(`${BASE}/templates`, templateBody(tpl, 'create')));

/** PUT is partial: pass a full template (converted) or just the fields to change, e.g. `{ active }`. */
export const updateTemplate = (key: string, tpl: EngagementTemplate | Partial<EngagementTemplate>) =>
  call<EngagementTemplate>(() =>
    API_V1.put(
      `${BASE}/templates/${encodeURIComponent(key)}`,
      'variants' in tpl && 'name' in tpl && 'recipientBenefit' in tpl ? templateBody(tpl as EngagementTemplate, 'update') : tpl
    )
  );

export const testTemplate = (key: string, variableValues?: Record<string, string>) =>
  call<{ testId?: string }>(() => API_V1.post(`${BASE}/templates/${encodeURIComponent(key)}/test`, { variableValues }));

// ------------------------------------------------------------------ Einstellungen, Wirkung

export const getEngagementSettings = () => call<EngagementSettings>(() => API_V1.get(`${BASE}/settings`));

/** PUT /settings is strict: only timeSaved, caps, sunsetAfterUnopened, killSwitch — never the switches. */
export const updateEngagementSettings = (settings: EngagementSettingsUpdate) =>
  call<EngagementSettings>(() =>
    API_V1.put(`${BASE}/settings`, {
      timeSaved: settings.timeSaved,
      caps: settings.caps,
      sunsetAfterUnopened: settings.sunsetAfterUnopened,
      killSwitch: settings.killSwitch,
    })
  );

export const getEngagementInsights = (days = 30) => call<EngagementInsights>(() => API_V1.get(`${BASE}/insights`, { params: { days } }));

// ------------------------------------------------------------------ Automationen, Aktionen, Bindung

export const getAutomations = () => call<unknown>(() => API_V1.get(`${BASE}/automations`));

export const patchAutomation = (key: string, patch: { enabled?: boolean; conditions?: Record<string, unknown> }) =>
  call<EngagementAutomation>(() => API_V1.patch(`${BASE}/automations/${encodeURIComponent(key)}`, patch));

export const dryRunAutomation = (key: string) =>
  call<AutomationDryRun>(() => API_V1.post(`${BASE}/automations/${encodeURIComponent(key)}/dry-run`));

export const getPromotions = () => call<unknown>(() => API_V1.get(`${BASE}/promotions`)).then((d) => asList<EngagementPromotion>(d));

/** The API body is flat: `percentOff`/`durationMonths` at the top, not under `discount`. */
export const promotionBody = (req: CreatePromotionRequest) => ({
  name: req.name.trim(),
  percentOff: req.discount.percentOff,
  durationMonths: req.discount.durationMonths,
  audience: req.audience,
  endsAfterDays: req.endsAfterDays,
  maxRedemptions: req.maxRedemptions,
  controlGroupPercent: req.controlGroupPercent,
  dryRun: req.dryRun,
});

export const createPromotion = (req: CreatePromotionRequest) =>
  call<PromotionPreview>(() => API_V1.post(`${BASE}/promotions`, promotionBody(req)));

export const endPromotion = (id: string) => call<EngagementPromotion>(() => API_V1.post(`${BASE}/promotions/${encodeURIComponent(id)}/end`));

export const getRetention = () => call<EngagementRetention>(() => API_V1.get(`${BASE}/retention`));

export const getTrialPipeline = (params: { dayFrom?: number; minReplies?: number } = {}) =>
  call<TrialPipeline>(() => API_V1.get(`${BASE}/trial-pipeline`, { params })).then((d) => ({
    items: d?.items ?? [],
    runningPromotion: d?.runningPromotion ?? null,
    computedAt: d?.computedAt,
  }));

// ------------------------------------------------------------------ Wachstum, Umsatz

/** GET /growth/funnel → `{ days, rows, controlSum, platforms, computedAt }`. */
export const getGrowthFunnel = (days = 30) =>
  call<Partial<GrowthFunnel>>(() => API_V1.get(`${BASE}/growth/funnel`, { params: { days } })).then(
    (d): GrowthFunnel => ({ rows: d?.rows ?? [], controlSum: d?.controlSum, platforms: d?.platforms })
  );

/** GET /growth/roas → `{ campaigns, status, reason?, totals, unmatched, withoutAds, … }`; rows = campaigns. */
export const getGrowthRoas = (days = 30) =>
  call<Omit<GrowthRoas, 'rows'> & { campaigns?: GrowthRoas['rows'] }>(() => API_V1.get(`${BASE}/growth/roas`, { params: { days } })).then(
    (d): GrowthRoas => ({
      rows: d?.campaigns ?? [],
      status: d?.status,
      reason: d?.reason,
      totals: d?.totals,
      unmatched: d?.unmatched,
      withoutAds: d?.withoutAds,
      controlSum: d?.controlSum,
    })
  );

interface ExperimentGroup {
  experimentId: string;
  daysRunning: number;
  requiredVisitors: number;
  variants: Omit<ExperimentVariantRow, 'experimentId'>[];
}

/** GET /growth/experiments → `[{ experimentId, daysRunning, requiredVisitors, variants: [...] }]`, flattened per variant. */
export const getGrowthExperiments = () =>
  call<ExperimentGroup[]>(() => API_V1.get(`${BASE}/growth/experiments`)).then((groups) =>
    (Array.isArray(groups) ? groups : []).flatMap((g) =>
      (g.variants ?? []).map((v) => ({ ...v, experimentId: g.experimentId }) as ExperimentVariantRow)
    )
  );

export const runRollup = () => call<unknown>(() => API_V1.post(`${BASE}/rollup/run`));
